import * as React from 'react';
import { Platform, Text } from 'react-native';

import { Colors } from 'theme/colors';

import { ICONS } from './svgIcons';

const DEFAULT_ICON_SIZE = 12;

const isFontello = (glyphMap: GlyphMapType): glyphMap is FontelloGlyphMap =>
  glyphMap && Object.prototype.hasOwnProperty.call(glyphMap, 'glyphs');

const isIcoMoon = (glyphMap: GlyphMapType): glyphMap is IcoMoonGlyphMap =>
  glyphMap && Object.prototype.hasOwnProperty.call(glyphMap, 'icons');

const isSvgIcon = (name?: string) => name && Object.prototype.hasOwnProperty.call(ICONS, name);

/**
 * Provides scalable Vector Icons given fonts, font glyphs, and image maps.
 *
 */
export class VectorIcon extends React.PureComponent<VectorIconProps> {
  public static defaultProps?: Partial<VectorIconProps>;
  /**
   * A reference to the font family name.
   */
  private readonly fontReference?: string;

  /**
   * A reference to the glyph map.
   */
  private readonly glyphMap: Partial<GlyphMap>;

  public constructor(props: VectorIconProps) {
    super(props);
    // Remove font family references containing file names
    this.fontReference =
      Platform.OS === 'android' && props.family
        ? props.family.replace(/\.(otf|ttf)$/, '')
        : props.family;

    // Load the appropriate font glyph map
    if (isFontello(props.config)) {
      this.glyphMap = props.config.glyphs.reduce((map: Partial<GlyphMap>, icon: FontelloIcon) => {
        map[icon.css] = icon.code;

        return map;
      }, {});
      this.fontReference = this.fontReference || props.config.name;
    } else if (isIcoMoon(props.config)) {
      this.glyphMap = props.config.icons.reduce((map: Partial<GlyphMap>, icon: IcoMoonIcon) => {
        map[icon.properties.name] = icon.properties.code;

        return map;
      }, {});
      this.fontReference =
        this.fontReference || props.config.preferences.fontPref.metadata.fontFamily;
    } else {
      this.glyphMap = props.config;
    }
  }

  public render() {
    const { name, size, color, forceUseGlyph, ...props } = this.props;
    let SvgIcon: SvgrComponent;
    let glyph = name ? this.glyphMap[name] : '';
    const hasSvgIcon = isSvgIcon(name);
    const hasGlyphIcon = glyph !== undefined;

    if (
      (hasSvgIcon && hasGlyphIcon && !forceUseGlyph) ||
      (hasSvgIcon && !hasGlyphIcon && forceUseGlyph) ||
      (hasSvgIcon && !forceUseGlyph)
    ) {
      SvgIcon = ICONS[name];
    } else if (!hasSvgIcon && !hasGlyphIcon) {
      glyph = '?';
    }

    if (typeof glyph === 'number') {
      glyph = String.fromCharCode(glyph);
    }

    const styleDefaults = {
      color,
      fontSize: size,
    };

    const styleOverrides = {
      fontFamily: this.fontReference,
      fontStyle: 'normal',
      fontWeight: 'normal',
    };

    const style = [styleDefaults, props.style, styleOverrides];

    return SvgIcon ? (
      <React.Suspense fallback="">
        <SvgIcon color={color} {...props} />
      </React.Suspense>
    ) : (
      <Text {...props} style={style}>
        {glyph}
        {this.props.children}
      </Text>
    );
  }
}

VectorIcon.defaultProps = {
  // allowFontScaling: false,
  // height: DEFAULT_ICON_SIZE,
  size: DEFAULT_ICON_SIZE,
  // width: DEFAULT_ICON_SIZE,
};

interface IcoMoonIcon {
  /** Ico moon font configuration */
  properties: {
    /** Unique code that maps to a specific icon */
    code: number;
    /** Name of the icon */
    name: string;
  };
}
interface IcoMoonGlyphMap {
  /** List of icons for a given icon configuration */
  icons: IcoMoonIcon[];
  /** Preferences for a given icon set */
  preferences: {
    /** Configuration of the preferences for a given font */
    fontPref: {
      /** Meta data about the font configuration */
      metadata: {
        /** Name of the font family to load for the icon set */
        fontFamily: string;
      };
    };
  };
}

interface FontelloIcon {
  /** Unique code that maps to a specific icon */
  code: number;
  /** CSS definition used to define the icon */
  css: string;
}

interface FontelloGlyphMap {
  /** List of icons for a given icon configuration */
  glyphs: FontelloIcon[];
  /** Name of the icon set */
  name: string;
}

interface GlyphMap {
  /** Unique mapping of an icon name to its code representation */
  [iconName: string]: number;
}

type GlyphMapType = IcoMoonGlyphMap | FontelloGlyphMap | GlyphMap;

export interface VectorIconProps {
  /** If true, font should be scaled independent of its internal configuration */
  readonly allowFontScaling?: boolean;
  /** React children to render inside of Icon */
  readonly children?: React.ReactNode;
  /** Color of the icon */
  readonly color?: string;
  /** Configuration to load for a given Icon Set */
  readonly config?: GlyphMapType;
  /** Font family to which the icon belongs */
  readonly family?: string;
  /** Height of the icon */
  readonly height?: number;
  /** Name of the icon */
  readonly name?: string;
  /** Size of the icon (in terms of font size) */
  readonly size?: number;
  /** Custom styles to apply */
  readonly style?: Record<string, unknown>;
  /** Width of the icon */
  readonly width?: number;
  /** Forces the use of glyph icons when true */
  readonly forceUseGlyph?: boolean;
  /** Used for complex svg icons where the outline represents the strokes on the svg */
  readonly fill?: string;
  /** Opacity of the icon */
  readonly opacity?: number;
}
